import { GetAllJourneysResponse, JourneyDetail } from '@fullstory/proto/fs/services/dx-objects';
import { JourneyMapRecord } from '@fullstory/proto/fs/services/search-settings';
import { combineReducers, createAppCacheReducer } from '@fullstory/redux-util/utils';
import { createAccessReducer } from '../../utils/accessControl';
import { createRequestReducer } from '@fullstory/redux-util';
import { JourneysActions } from './actions';
import { JourneysState, JourneysRequestTypes, JourneysById, UnnamedJourneyState } from './types';
import { ComputeJourneyMapResponse } from '@fullstory/proto/fs/services/alexa';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const getJourneyMapId = (JourneyMap: JourneyMapRecord | null): string | undefined =>
  JourneyMap?.journeyMap?.id;

// Force JourneyMapRecord rsp to conform to latest client JourneyMap/Constraints model
const initJourney = (journeyDetail: JourneyDetail): JourneyDetail => {
  const jm = journeyDetail.journey?.journeyMap?.journeyMap;
  if (jm) {
    journeyDetail.journey!.journeyMap!.journeyMap = {
      pivot: jm.pivot ?? [],
      pivotSequence: jm.pivotSequence ?? null,
      interactions: jm.interactions ?? [],
      eventIdent: jm.eventIdent ?? null,
      syntheticTypes: jm.syntheticTypes ?? null,
      constraints: {
        inSameSession: jm.constraints?.inSameSession ?? false,
        timeRange: jm.constraints?.timeRange ?? null,
        completeSessions: jm.constraints?.completeSessions ?? false,
      },
      latestPivot: jm.latestPivot ?? false,
      collapseRepeated: jm.collapseRepeated ?? false,
    };
  }
  return journeyDetail;
};

const extractJourneyIdFromBody = (body: { journeyId: string } | { journeyMapId: string }): string =>
  'journeyId' in body ? body.journeyId : body.journeyMapId;

export const journeys = (
  state: JourneysState = {},
  action: JourneysActions | AppBackendActions | SpaceActions | BulkRequestActions,
): JourneysState => {
  switch (action.type) {
    case JourneysRequestTypes.GetAllJourneysSuccess: {
      return action.data.journeys.reduce<JourneysById>(
        (acc, val) => {
          const id = getJourneyMapId(val.journey);

          if (id != null) acc[id] = initJourney(val);

          return acc;
        },
        { ...state },
      );
    }
    case JourneysRequestTypes.SetJourneySuccess: {
      const journeyMapRecord = action.data.journey?.journey ?? null;
      const id = getJourneyMapId(journeyMapRecord);

      return action.data.journey == null || id == null
        ? state
        : { ...state, [id]: initJourney(action.data.journey) };
    }
    case JourneysRequestTypes.MarkJourneyViewedSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { lastViewed } = action.data;
      const journeyId = extractJourneyIdFromBody(body);
      const journey = state?.[journeyId];

      return journey == null ? state : { ...state, [journeyId]: { ...journey, lastViewed } };
    }
    case JourneysRequestTypes.StarJourneySuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const journeyId = extractJourneyIdFromBody(body);
      const { starred } = body;
      const journey = state?.[journeyId];

      return journey == null ? state : { ...state, [journeyId]: { ...journey, starred } };
    }
    case JourneysRequestTypes.ArchiveJourneySuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const journeyId = extractJourneyIdFromBody(body);
      const { archived } = body;
      const journey = state?.[journeyId];
      const journeyMapRecord = journey?.journey;
      const metadata = journeyMapRecord?.metadata;

      return journey == null || journeyMapRecord == null || metadata == null
        ? state
        : {
            ...state,
            [journeyId]: {
              ...journey,
              journey: {
                ...journeyMapRecord,
                metadata: { ...metadata, archived },
              },
            },
          };
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const journeysReq = body.journeys;

      if (journeysReq != null) {
        journeysReq.journeys.forEach(m => {
          const { journeyId, archived } = m;
          const journey = newState?.[journeyId];
          const journeyRecord = journey?.journey;
          const metadata = journeyRecord?.metadata;

          if (journey && journeyRecord && metadata) {
            newState = {
              ...newState,
              [journeyId]: {
                ...journey,
                journey: {
                  ...journeyRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
    case AppBackendRequestTypes.GetSidebarSuccess:
    case AppBackendRequestTypes.GetJourneyDetailViewSuccess: {
      if (!action.data.deps?.journeys) return state;
      return { ...state, ...action.data.deps.journeys };
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const journeys = action.data.objects.reduce<JourneysState>((acc, o) => {
        if (o.journey?.journey?.journeyMap?.id) acc[o.journey.journey.journeyMap.id] = o.journey;
        return acc;
      }, {});

      return Object.keys(journeys).length
        ? {
            ...state,
            ...journeys,
          }
        : state;
    }
  }
  return state;
};

export const unnamedJourney = (
  state: UnnamedJourneyState | null = null,
  action: JourneysActions,
): UnnamedJourneyState | null => {
  switch (action.type) {
    case JourneysRequestTypes.CreateUnnamedJourneyRequest: {
      const body = action.body;
      if (!body) return null;
      return body;
    }
    case JourneysRequestTypes.GetUnnamedJourneyRequest: {
      return state;
    }
    case JourneysRequestTypes.CreateUnnamedJourneyFailure:
    case JourneysRequestTypes.GetUnnamedJourneyFailure: {
      return null;
    }
    case JourneysRequestTypes.CreateUnnamedJourneySuccess: {
      const body = action.request.body;
      if (body === null || body === undefined) return null;
      return body;
    }
    case JourneysRequestTypes.GetUnnamedJourneySuccess: {
      return action.data;
    }
  }
  return state;
};

const allJourneys = createRequestReducer<GetAllJourneysResponse>()(
  JourneysRequestTypes.GetAllJourneysRequest,
  JourneysRequestTypes.GetAllJourneysSuccess,
  JourneysRequestTypes.GetAllJourneysFailure,
);

const access = createAccessReducer(
  JourneysRequestTypes.GetJourneyAccessRequest,
  JourneysRequestTypes.GetJourneyAccessSuccess,
  JourneysRequestTypes.GetJourneyAccessFailure,
);

const journeyMetrics = createAppCacheReducer<ComputeJourneyMapResponse>()(
  JourneysRequestTypes.JourneysAppCacheIsLoading,
  JourneysRequestTypes.JourneysAppCacheLoadFailed,
  JourneysRequestTypes.JourneysAppCacheRefetching,
  JourneysRequestTypes.JourneysAppCacheHasData,
  JourneysRequestTypes.JourneysAppCacheFresh,
  JourneysRequestTypes.JourneysAppCacheSwap,
  JourneysRequestTypes.JourneysAppCacheClear,
)();

type JourneysReducerState = {
  allJourneys: ReturnType<typeof allJourneys>;
  journeys: ReturnType<typeof journeys>;
  access: ReturnType<typeof access>;
  journeyMetrics: ReturnType<typeof journeyMetrics>;
  unnamedJourney: ReturnType<typeof unnamedJourney>;
};

export const journeyMapsReducer = combineReducers<JourneysReducerState>({
  allJourneys,
  journeys,
  access,
  journeyMetrics,
  unnamedJourney,
});
